import XAPI, { type Statement } from '@xapi/xapi';
import { useCallback, useMemo } from 'react';

/**
 * Initializes an xAPI client from base64-encoded LMS payload
 * @param lmsPayloadRaw - Base64-encoded payload containing endpoint and auth
 * @returns Initialized XAPI client or null if initialization fails
 */
export const initializeXAPI = (lmsPayloadRaw: string | null): XAPI | null => {
  if (!lmsPayloadRaw) return null;

  try {
    const lmsPayload: { endpoint: string; auth: string } = JSON.parse(atob(lmsPayloadRaw));

    if (lmsPayload.endpoint && lmsPayload.auth) {
      return new XAPI({
        endpoint: lmsPayload.endpoint,
        auth: lmsPayload.auth,
      });
    }
  } catch (error) {
    console.error('Failed to initialize XAPI:', error);
  }

  return null;
};

/**
 * Sends xAPI statements for a given room
 * @param xapi - The initialized xAPI client
 * @param roomCode - The room identifier
 * @param roomProgress - The progress percentage (0-100)
 * @param userEmail - The user's email address
 */
export const sendXAPIStatement = (xapi: XAPI, roomCode: string, roomProgress: number, userEmail: string) => {
  if (roomProgress > 0) {
    const statement: Statement = {
      actor: {
        objectType: 'Agent',
        mbox: `mailto:${userEmail}`,
      },
      verb: XAPI.Verbs.PROGRESSED,
      object: {
        objectType: 'Activity',
        id: `https://tryhackme.com/rooms/${roomCode}`,
      },
      result: {
        score: {
          raw: roomProgress,
          min: 0,
          max: 100,
          scaled: roomProgress / 100,
        },
      },
    };

    xapi.sendStatement({ statement });
  }

  if (roomProgress === 100) {
    const statement: Statement = {
      actor: {
        objectType: 'Agent',
        mbox: `mailto:${userEmail}`,
      },
      verb: XAPI.Verbs.COMPLETED,
      object: {
        objectType: 'Activity',
        id: `https://tryhackme.com/rooms/${roomCode}`,
      },
    };

    xapi.sendStatement({ statement });
  }
};

/**
 * Custom hook to handle xAPI communication for a given room
 * @param lmsRoom - The LMS room identifier
 * @param lmsPayloadRaw - Base64-encoded payload containing endpoint and auth
 * @param roomCode - The room identifier
 * @param userEmail - The user's email address
 */
export const useXAPI = (lmsRoom: string | null, lmsPayloadRaw: string | null, roomCode: string, userEmail: string) => {
  const xapi = useMemo(() => initializeXAPI(lmsPayloadRaw), [lmsPayloadRaw]);

  const handleXAPI = useCallback(
    (roomProgress: number) => {
      if (xapi && lmsRoom === roomCode) {
        sendXAPIStatement(xapi, roomCode, roomProgress, userEmail);
      }
    },
    [xapi, lmsRoom, roomCode, userEmail],
  );

  if (!lmsRoom || !lmsPayloadRaw) return null;

  return handleXAPI;
};
